import {
	ReactElement,
	useCallback,
	useState
} from "react";
import {
	Dialog,
	FormattedText,
	Toolbar
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { useDataInputDialog } from "client/Hooks";
import { NameConflictResolutionKind } from "client/Api/Documents/common";

type ResolveResult = {
	resolution: NameConflictResolutionKind;
	applyToRemaining: boolean;
};

export type UploadNameConflictResolutionDialogProps = {
	itemName: string;
	hasMoreItemsInBatch: boolean;
	onResolve(result: ResolveResult): void;
};

export const UploadNameConflictResolutionDialog = (props: UploadNameConflictResolutionDialogProps): ReactElement => {
	const {
		itemName,
		hasMoreItemsInBatch,
		onResolve
	} = props;

	const [applyToRemaining, setApplyToRemaining] = useState<boolean>(false);

	const skipHandler = (): void => {
		onResolve({
			resolution: NameConflictResolutionKind.Skip,
			applyToRemaining
		});
	};

	const replaceHandler = (): void => {
		onResolve({
			resolution: NameConflictResolutionKind.Replace,
			applyToRemaining
		});
	};

	const keepBothHandler = (): void => {
		onResolve({
			resolution: NameConflictResolutionKind.KeepBoth,
			applyToRemaining
		});
	};

	return (
		<Dialog>
			<Dialog.Header
				title={localize("_ReplaceConfirmation")}
			/>
			<Dialog.Body>
				<FormattedText
					text={localize("_UploadNameConflictResolutionDialogBody")}
					thingsToReplace={[
						{ what: "{{ItemName}}", filler: itemName }
					]}
				/>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					{hasMoreItemsInBatch && (
						<Toolbar.Checkbox
							text={localize("_ApplyToAll")}
							value={applyToRemaining}
							onChange={setApplyToRemaining}
						/>
					)}
					<Toolbar.SpaceBetween />
					<Toolbar.Button kind="primary" onClick={replaceHandler} text={localize("_Replace")} />
					<Toolbar.Button kind="primary" onClick={keepBothHandler} text={localize("_KeepBoth")} />
					<Toolbar.Button kind="secondary" onClick={skipHandler} text={localize("_Skip")} />
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
UploadNameConflictResolutionDialog.displayName = "UploadNameConflictResolutionDialog";

export type UploadNameConflictResolutionDialogInitialData = Omit<UploadNameConflictResolutionDialogProps, "onResolve">;

export function useUploadNameConflictResolutionDialog(): [
	element: ReactElement | null,
	open: (props: UploadNameConflictResolutionDialogInitialData) => Promise<ResolveResult>
] {
	const [element, originalOpen] = useDataInputDialog<UploadNameConflictResolutionDialogInitialData, ResolveResult>(
		(props, _, onConfirmation) => (
			<UploadNameConflictResolutionDialog
				{...props}
				onResolve={onConfirmation}
			/>
		)
	);

	const open = useCallback(
		async (props: UploadNameConflictResolutionDialogInitialData): Promise<ResolveResult> => {
			const result: TryResult<ResolveResult> = await originalOpen(props);

			if (!result.wasSuccessful) {
				throw new Error("The UploadNameConflictResolutionDialog does not support onClose");
			}

			return result.result;
		},
		[originalOpen]
	);

	return [element, open];
}
